import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  Code,
  Link as LinkIcon,
  Paperclip,
  X,
  FileText,
  Image as ImageIcon,
  Loader2,
} from 'lucide-react';
import { useRef, useState, useCallback, useEffect, KeyboardEvent } from 'react';
import { uploadApi, UploadedAttachment } from '@/lib/uploadApi';

interface CommentEditorProps {
  workspaceId: string;
  onSubmit: (content: string, contentHtml: string, attachmentIds: string[]) => void;
  placeholder?: string;
  submitLabel?: string;
  disabled?: boolean;
  compact?: boolean;
  autoFocus?: boolean;
  initialContent?: string;
  onCancel?: () => void;
}

interface PendingAttachment {
  id: string;
  original_name: string;
  mime_type: string;
  size: number;
  url: string;
  is_image: boolean;
}

function ToolbarButton({
  active,
  onClick,
  title,
  children,
  disabled,
}: {
  active?: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-1 rounded transition-colors ${
        active
          ? 'bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300'
          : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200'
      } disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function CommentEditor({
  workspaceId,
  onSubmit,
  placeholder = 'Write a comment...',
  submitLabel = 'Post Comment',
  disabled = false,
  compact = false,
  autoFocus = false,
  initialContent,
  onCancel,
}: CommentEditorProps) {
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [showLinkInput, setShowLinkInput] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        horizontalRule: false,
        blockquote: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-brand-600 underline hover:text-brand-700 dark:text-brand-400',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    editorProps: {
      attributes: {
        class: `prose prose-sm dark:prose-invert max-w-none focus:outline-none min-h-[${compact ? '40' : '60'}px] max-h-[200px] overflow-y-auto px-3 py-2`,
      },
    },
    content: initialContent || '',
    autofocus: autoFocus,
  });

  useEffect(() => {
    return () => {
      editor?.destroy();
    };
  }, []);

  const handleSubmit = useCallback(() => {
    if (!editor) return;
    const html = editor.getHTML();
    const text = editor.getText().trim();
    if (!text && attachments.length === 0) return;

    onSubmit(text, html, attachments.map((a) => a.id));
    editor.commands.clearContent();
    setAttachments([]);
  }, [editor, attachments, onSubmit]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  const handleFileSelect = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      setUploading(true);
      try {
        const newAttachments: PendingAttachment[] = [];
        for (const file of Array.from(files)) {
          const result: UploadedAttachment = await uploadApi.upload(
            workspaceId,
            file,
            'comments'
          );
          newAttachments.push({
            id: result.id,
            original_name: result.original_name,
            mime_type: result.mime_type,
            size: result.size,
            url: result.url,
            is_image: result.is_image,
          });
        }
        setAttachments((prev) => [...prev, ...newAttachments]);
      } catch (err) {
        console.error('Failed to upload file:', err);
      } finally {
        setUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [workspaceId]
  );

  const removeAttachment = useCallback(
    async (id: string) => {
      try {
        await uploadApi.delete(workspaceId, id);
      } catch (err) {
        console.error('Failed to delete attachment:', err);
      }
      setAttachments((prev) => prev.filter((a) => a.id !== id));
    },
    [workspaceId]
  );

  const setLink = useCallback(() => {
    if (!editor) return;
    if (linkUrl) {
      const url = linkUrl.startsWith('http') ? linkUrl : `https://${linkUrl}`;
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    } else {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    }
    setShowLinkInput(false);
    setLinkUrl('');
  }, [editor, linkUrl]);

  if (!editor) return null;

  const iconSize = compact ? 14 : 15;

  return (
    <div
      className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-brand-500 focus-within:border-brand-500 transition-shadow bg-white dark:bg-gray-800"
      onKeyDown={handleKeyDown}
    >
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
        <ToolbarButton
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Bold (⌘B)"
          disabled={disabled}
        >
          <Bold size={iconSize} />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Italic (⌘I)"
          disabled={disabled}
        >
          <Italic size={iconSize} />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          title="Underline (⌘U)"
          disabled={disabled}
        >
          <UnderlineIcon size={iconSize} />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          title="Strikethrough"
          disabled={disabled}
        >
          <Strikethrough size={iconSize} />
        </ToolbarButton>

        <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1" />

        <ToolbarButton
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Bullet List"
          disabled={disabled}
        >
          <List size={iconSize} />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Numbered List"
          disabled={disabled}
        >
          <ListOrdered size={iconSize} />
        </ToolbarButton>

        <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1" />

        <ToolbarButton
          active={editor.isActive('code')}
          onClick={() => editor.chain().focus().toggleCode().run()}
          title="Inline Code"
          disabled={disabled}
        >
          <Code size={iconSize} />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('link')}
          onClick={() => {
            if (editor.isActive('link')) {
              editor.chain().focus().unsetLink().run();
            } else {
              setShowLinkInput(true);
            }
          }}
          title="Link"
          disabled={disabled}
        >
          <LinkIcon size={iconSize} />
        </ToolbarButton>

        <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1" />

        <ToolbarButton
          onClick={() => fileInputRef.current?.click()}
          title="Attach file"
          disabled={disabled || uploading}
        >
          {uploading ? (
            <Loader2 size={iconSize} className="animate-spin" />
          ) : (
            <Paperclip size={iconSize} />
          )}
        </ToolbarButton>
      </div>

      {/* Link input popover */}
      {showLinkInput && (
        <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <input
            type="url"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                setLink();
              }
              if (e.key === 'Escape') {
                setShowLinkInput(false);
                setLinkUrl('');
              }
            }}
            placeholder="https://example.com"
            className="flex-1 text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-brand-500"
            autoFocus
          />
          <button
            type="button"
            onClick={setLink}
            className="text-xs px-2 py-1 bg-brand-600 text-white rounded hover:bg-brand-700"
          >
            Apply
          </button>
          <button
            type="button"
            onClick={() => {
              setShowLinkInput(false);
              setLinkUrl('');
            }}
            className="text-xs px-2 py-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Editor content */}
      <EditorContent editor={editor} />

      {/* Attachments preview */}
      {attachments.length > 0 && (
        <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700 space-y-1.5">
          {attachments.map((att) => (
            <div
              key={att.id}
              className="flex items-center gap-2 text-xs bg-gray-50 dark:bg-gray-700/50 rounded px-2 py-1.5"
            >
              {att.is_image ? (
                <ImageIcon size={14} className="text-blue-500 flex-shrink-0" />
              ) : (
                <FileText size={14} className="text-gray-400 flex-shrink-0" />
              )}
              <span className="truncate text-gray-700 dark:text-gray-300 flex-1">
                {att.original_name}
              </span>
              <span className="text-gray-400 flex-shrink-0">
                {formatFileSize(att.size)}
              </span>
              <button
                type="button"
                onClick={() => removeAttachment(att.id)}
                className="text-gray-400 hover:text-red-500 flex-shrink-0"
                title="Remove"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Footer with submit */}
      <div className="flex items-center justify-between px-3 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
        <span className="text-xs text-gray-400 dark:text-gray-500">
          {compact ? '⌘↵ to send' : 'Ctrl+Enter to send'}
        </span>
        <div className="flex items-center gap-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={disabled || (!editor.getText().trim() && attachments.length === 0)}
            className="px-3 py-1.5 text-sm bg-brand-600 text-white rounded hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitLabel}
          </button>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => handleFileSelect(e.target.files)}
      />
    </div>
  );
}
